import {
  siloAbi,
  siloIncentivesControllerAbi,
  siloLensAbi,
} from "@silo/contract-abi-types";
import {
  Address,
  ChainPublicClientService,
  getChainKeyFromProtocol,
  makeAmount,
  PublicClientService,
} from "@silo/types";
import { calculateRewardsApr, convertToUsd } from "@silo/utils";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "../contracts-client/types";
import { GLOBAL_CONFIG } from "@silo/config";
import { KvClient } from "../kv-client";
import { siloLensAddress } from "@silo/contract-addresses";
import { match } from "ts-pattern";
import { KvClientError, KvService } from "../kv-client/types";
import { NoSuchElementException } from "effect/Cause";
import { ParseError } from "@effect/schema/ParseResult";
import { RevenueData } from "./types";
import { isNullable } from "effect/Predicate";

const {
  rewardTokenAddress,
  protocolKey,
  rewardTokenDecimals,
  marketAddress,
  targetTokenAddress,
  targetTokenDecimals,
  shareTokenType,
  shareTokenAddress,
} = GLOBAL_CONFIG.revenue;

const chainKey = getChainKeyFromProtocol(protocolKey);

export function getRevenueData(
  user: Address | null
): Effect.Effect<
  RevenueData,
  ContractsClientError | KvClientError | ParseError | NoSuchElementException,
  KvService | PublicClientService
> {
  return Effect.gen(function* () {
    const { publicClient } = yield* ChainPublicClientService;

    const siloIncentivesControllerContract = getContract({
      address: GLOBAL_CONFIG.revenue.siloIncentivesControllerAddress,
      abi: siloIncentivesControllerAbi,
      client: publicClient,
    });

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const siloContract = getContract({
      address: marketAddress,
      abi: siloAbi,
      client: publicClient,
    });

    const { emissionPerSecond, totalTokens, revenueBalance } =
      yield* Effect.tryPromise({
        try: async () => {
          function getCollateralTotal(): Promise<bigint> {
            return siloLensContract.read.totalDepositsWithInterest([
              marketAddress,
              targetTokenAddress,
            ]);
          }

          function getProtectedTotal(): Promise<bigint> {
            return siloContract.read
              .assetStorage([targetTokenAddress])
              .then((storage) => storage.collateralOnlyDeposits);
          }

          function getDebtTotal(): Promise<bigint> {
            return siloLensContract.read.totalBorrowAmountWithInterest([
              marketAddress,
              targetTokenAddress,
            ]);
          }

          async function getRevenueBalance(): Promise<bigint> {
            if (isNullable(user)) {
              return 0n;
            }

            return siloIncentivesControllerContract.read.getRewardsBalance([
              [shareTokenAddress],
              user,
            ]);
          }

          const [[, emissionPerSecond], totalTokens, revenueBalance] =
            await Promise.all([
              siloIncentivesControllerContract.read.getAssetData([
                shareTokenAddress,
              ]),
              match(shareTokenType)
                .with("collateral", () => getCollateralTotal())
                .with("protected", () => getProtectedTotal())
                .with("debt", () => getDebtTotal())
                .exhaustive(),
              getRevenueBalance(),
            ]);

          return {
            emissionPerSecond: makeAmount(emissionPerSecond),
            totalTokens: makeAmount(totalTokens),
            revenueBalance: makeAmount(revenueBalance),
          };
        },
        catch: (cause) => new ContractsClientError({ cause }),
      });

    const tokenPriceMap = yield* KvClient.getTokenPriceMap([
      { tokenAddress: rewardTokenAddress, chainKey },
      { tokenAddress: targetTokenAddress, chainKey },
    ]);

    const rewardTokenPriceUsd = yield* pipe(
      tokenPriceMap.get({
        tokenAddress: rewardTokenAddress,
        chainKey,
      }),
      Effect.mapError(
        (cause) =>
          new KvClientError({
            cause,
            message: `Missing price for ${rewardTokenAddress} on ${chainKey}`,
          })
      )
    );

    const tokenPriceUsd = yield* pipe(
      tokenPriceMap.get({
        tokenAddress: targetTokenAddress,
        chainKey,
      }),
      Effect.mapError(
        (cause) =>
          new KvClientError({
            cause,
            message: `Missing price for ${targetTokenAddress} on ${chainKey}`,
          })
      )
    );

    const revenueApr = calculateRewardsApr({
      emissionPerSecond,
      rewardTokenPriceUsd,
      rewardTokenDecimals,
      totalTokens,
      tokenPriceUsd,
      tokenDecimals: targetTokenDecimals,
    });

    const revenueBalanceUsd = convertToUsd(
      revenueBalance,
      rewardTokenDecimals,
      rewardTokenPriceUsd
    );

    const revenueData: RevenueData = {
      revenueApr,
      emissionPerSecond,
      rewardTokenPriceUsd,
      totalTokens,
      tokenPriceUsd,
      revenueBalance,
      revenueBalanceUsd,
    };

    return revenueData;
  }).pipe(
    // TODO: abstraction for providing chain public client by protocolKey
    Effect.provideServiceEffect(
      ChainPublicClientService,
      Effect.gen(function* () {
        const { getPublicClient } = yield* PublicClientService;
        const publicClient = getPublicClient(protocolKey);

        return { publicClient };
      })
    )
  );
}
